import React, { useEffect, useState } from 'react';
import { Coins, TrendingUp, Users, ShoppingBag, Flame, Loader2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { authFetch } from '../utils/authUtils';
import Sparkline from './Sparkline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const WINDOWS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const ShopAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await authFetch(
          `${BACKEND_URL}/api/admin/shop/analytics?days=${days}`,
        );
        if (r.ok && !cancel) setData(await r.json());
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [days]);

  const peakDay =
    (data?.per_day || []).reduce(
      (best, d) => (d.count > (best?.count || 0) ? d : best),
      null,
    ) || null;

  const topItem = (data?.per_item || [])[0] || null;

  return (
    <div className="space-y-6" data-testid="shop-analytics">
      {/* Window selector */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400 uppercase tracking-wide">Window</span>
        <div className="flex gap-1 p-1 bg-slate-800/70 border border-slate-700 rounded-full">
          {WINDOWS.map((w) => (
            <button
              key={w.days}
              onClick={() => setDays(w.days)}
              data-testid={`analytics-window-${w.days}`}
              className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                days === w.days
                  ? 'bg-amber-500 text-slate-900'
                  : 'text-gray-300 hover:bg-slate-700'
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
        {data?.current_week && (
          <span className="ml-auto text-xs text-gray-400">
            Current week · <span className="text-amber-300">{data.current_week}</span>
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading analytics…
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi
              icon={<ShoppingBag className="w-4 h-4" />}
              label="Redemptions"
              value={data?.totals?.redemptions ?? 0}
              accent="amber"
              testId="kpi-redemptions"
            />
            <Kpi
              icon={<Coins className="w-4 h-4" />}
              label="Coins spent"
              value={(data?.totals?.coins_spent ?? 0).toLocaleString()}
              accent="yellow"
              testId="kpi-coins-spent"
            />
            <Kpi
              icon={<Users className="w-4 h-4" />}
              label="Unique buyers"
              value={data?.totals?.unique_redeemers ?? 0}
              accent="indigo"
              testId="kpi-unique-buyers"
            />
            <Kpi
              icon={<Flame className="w-4 h-4" />}
              label="Peak day"
              value={peakDay ? `${peakDay.count} on ${peakDay.day}` : '—'}
              accent="rose"
              testId="kpi-peak-day"
            />
          </div>

          {/* Per-item */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-amber-300" />
                <h3 className="text-white font-semibold text-sm uppercase tracking-wide">
                  Top items · {days}d
                </h3>
                {topItem && (
                  <span className="ml-auto text-[11px] text-emerald-300 bg-emerald-900/40 border border-emerald-700/50 px-2 py-0.5 rounded-full font-semibold">
                    Leader: {topItem.title}
                  </span>
                )}
              </div>
              {(data?.per_item || []).length === 0 ? (
                <p className="text-sm text-gray-400">No redemptions yet.</p>
              ) : (
                <div className="space-y-2">
                  {(data?.per_item || []).map((row) => {
                    const maxCount = data.per_item[0].count;
                    const pct =
                      maxCount > 0 ? Math.max(6, (row.count / maxCount) * 100) : 0;
                    return (
                      <div
                        key={row.item_id}
                        className="flex items-center gap-3"
                        data-testid={`analytics-item-${row.item_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm text-white truncate">
                              {row.title || row.item_id}
                            </span>
                            {row.is_featured && (
                              <span className="text-[10px] font-bold text-amber-300 bg-amber-900/40 px-1.5 py-0.5 rounded-full">
                                WEEKLY
                              </span>
                            )}
                          </div>
                          <div className="h-2 rounded-full bg-slate-900/60 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-amber-500 to-orange-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                        <div className="w-28 text-right text-xs font-mono text-gray-300">
                          <span className="text-white font-bold">{row.count}</span> ·{' '}
                          <span className="text-amber-300">
                            {row.coins.toLocaleString()}c
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Per-day sparkline-ish */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold text-sm uppercase tracking-wide">
                  Daily redemptions
                </h3>
                {(data?.per_day || []).length > 1 && (
                  <Sparkline
                    values={(data?.per_day || []).map((d) => Number(d.count || 0))}
                    width={140}
                    height={32}
                    stroke="#fb923c"
                    fill="rgba(251,146,60,0.18)"
                  />
                )}
              </div>
              {(data?.per_day || []).length === 0 ? (
                <p className="text-sm text-gray-400">No activity in this window.</p>
              ) : (
                <div className="flex items-end gap-1 h-28">
                  {(() => {
                    const max = Math.max(
                      ...(data?.per_day || []).map((d) => d.count),
                      1,
                    );
                    return (data?.per_day || []).map((d) => {
                      const h = Math.max(4, (d.count / max) * 100);
                      return (
                        <div
                          key={d.day}
                          className="flex-1 flex flex-col items-center justify-end gap-1 min-w-[12px]"
                          title={`${d.day}: ${d.count} redemptions`}
                          data-testid={`analytics-day-${d.day}`}
                        >
                          <div
                            className="w-full rounded-sm bg-gradient-to-t from-amber-600 to-orange-400"
                            style={{ height: `${h}%` }}
                          />
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rotation history */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-5">
              <h3 className="text-white font-semibold text-sm uppercase tracking-wide mb-4">
                Rotation history
              </h3>
              {(data?.rotations || []).length === 0 ? (
                <p className="text-sm text-gray-400">No rotations recorded yet.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {(data?.rotations || []).map((r) => (
                    <div
                      key={r.week}
                      className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700/50"
                      data-testid={`rotation-${r.week}`}
                    >
                      <span className="text-xs font-mono text-amber-300 bg-amber-900/30 px-2 py-1 rounded">
                        {r.week}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{r.featured_title}</p>
                        <p className="text-[11px] text-gray-400">{r.featured_id}</p>
                      </div>
                      <span className="text-xs font-mono text-gray-400">
                        <span className="line-through text-gray-500 mr-1">
                          {r.original_cost}
                        </span>
                        <span className="text-amber-300 font-bold">
                          {r.discounted_cost}c
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

const Kpi = ({ icon, label, value, accent, testId }) => {
  const accents = {
    amber: 'from-amber-600/30 to-orange-600/30 border-amber-700/50 text-amber-200',
    yellow: 'from-yellow-600/30 to-amber-600/30 border-yellow-700/50 text-yellow-200',
    indigo: 'from-indigo-600/30 to-purple-600/30 border-indigo-700/50 text-indigo-200',
    rose: 'from-rose-600/30 to-pink-600/30 border-rose-700/50 text-rose-200',
  };
  return (
    <div
      className={`p-4 rounded-xl bg-gradient-to-br ${accents[accent]} border`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 mb-1 opacity-80">
        {icon}
        <span className="text-[11px] uppercase tracking-wider font-semibold">
          {label}
        </span>
      </div>
      <p className="text-xl font-bold text-white break-words">{value}</p>
    </div>
  );
};

export default ShopAnalytics;
